/**
 * Presents a UI suited for viewing permissions associated with a selected consumer.
 * @module app/views/admin/permissions/Viewer
 */
import Backbone from "backbone";
import _ from "underscore";
import Permission from "app/models/Permission";
import PermissionUtil from "@HBKEngineering/permission-util";
import Field from "core/models/Field";
import CollectionTableView from "core/views/CollectionTableView";
import SelectView from "core/views/SelectView";
import ErrorUtils from "app/utils/ErrorUtils";
import ViewHelper from "app/views/admin/permissions/ViewerHelper";
import modals from "core/views/modals";
import urlService from "app/UrlService";

//yay hard-coding!
function producerMetadata(key) {
  var metadata = {
    Layout: {
      nameField: "name",
      urlBase: urlService.apiurl("/layerconfiguration/"),
      navBase: function (id) {
        return urlService.url("/map/layout/") + id;
      },
    },
    MapService: {
      nameField: "name",
      urlBase: urlService.apiurl("/mapservice/"),
      navBase: urlService.url("/map/service/"),
    },
    Plugin: {
      nameField: "Name",
      urlBase: urlService.apiurl("/Application/"),
      navBase: urlService.url("/admin/plugins/Details/"),
    },
    DataType: {
      nameField: "Name",
      urlBase: urlService.apiurl("/layers/"),
    },
  };

  return metadata[key];
}

export default Backbone.View.extend({
  /**
   * Initializes the interface.
   * @param  {object} options The options hash.
   * @param {GroupCollection} options.groups A set of `Group` models to use in searching for a consumer.
   * @param {UserCollection} options.users A set of `User` models to use in searching for a consumer.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      users: new Backbone.Collection([]),
      groups: new Backbone.Collection([]),
    });
  },

  className: "permissions-viewer",

  template: _.template(
    '<div class="permission-search-container"></div><div class="permission-section public-section"><h2>Public Permissions</h2></div><div class="permission-section group-section"><h2>Group Permissions</h2></div><div class="permission-section inherited-section"><h2>All Inherited Permissions</h2></div><div class="permission-section user-section"><h2>User Permissions</h2></div><div class="permission-section overall-section"><h2>Overall Permissions</h2></div>'
  ),

  render: function () {
    this.$el.html(this.template(this.options));
    this._clearResults();
    this.select();

    var userEntries = _.map(this.options.users.models, function (user) {
      return {
        label: "(User) " + user.get("Username"),
        value: user.id,
      };
    });

    userEntries = _.sortBy(userEntries, "label");

    var groupEntries = _.map(this.options.groups.models, function (group) {
      return {
        label: "(Group) " + group.get("name"),
        value: group.id,
      };
    });

    groupEntries = _.sortBy(groupEntries, "label");

    var selectView = new SelectView({
      items: _.map(userEntries.concat(groupEntries), function (entry) {
        return {
          text: entry.label,
          value: entry.value,
        };
      }),
    });

    selectView.render();
    selectView.on("change", this.select, this);

    this.$(".permission-search-container").append(selectView.$el);
  },

  /**
   * Displays the permissions for the given consumer.
   * @param  {string} consumerId The id of the consumer to select.
   */
  select: function (consumerId) {
    modals.loading.show("Retrieving Permission Information");
    this._clearResults();

    var groups = [];
    var self = this;

    var consumers = [Permission];
    var selected;
    var selectedConsumer = this.options.users.get(consumerId);

    if (selectedConsumer) {
      selectedConsumer.getRoles().done(function (roles) {
        groups = _.filter(self.options.groups.models, function (group) {
          return _.contains(roles, group.get("name"));
        });

        selected = selectedConsumer;
        selectedConsumer = [selectedConsumer];

        gotGroups();
      });
    } else {
      selectedConsumer = this.options.groups.get(consumerId);

      if (selectedConsumer) {
        groups.push(selectedConsumer);
      }

      selectedConsumer = [];
      gotGroups();
    }

    function gotGroups() {
      consumers = consumers.concat(groups).concat(selectedConsumer);
      Permission.find(consumers, false, gotResults);
    }

    function gotResults(err, results) {
      if (err) {
        modals.loading.hide();
        ErrorUtils.handleError(
          {
            activity: "loading permission data",
          },
          err
        );
      } else {
        modals.loading.hide();
        self._renderResults(results, groups, selected);
      }
    }
  },

  /**
   * Clears search results.
   */
  _clearResults: function () {
    this.$(".permission-section").hide();
    this.$(".permission-section h3, .permission-section table").remove();
    this.$(".public-section").show();
  },

  /**
   * Renders the results of a selection operation.
   * @param  {Object} results          A mapping from consumer id to permission set.
   * @param  {Array} groups           The groups included in the search.
   * @param  {User} user The user selected. May be null, indicating that a user was not selected.
   */
  _renderResults: function (results, groups, user) {
    var publicSet = results[Permission.id];
    var groupSets = _.map(
      groups,
      function (group) {
        return results[group.id];
      },
      this
    );
    var consumerSet,
      unionedSet, //union of group permissions
      inheritedSet, //inheritance of group permissions and public permissions
      overallSet; //the "final" permission set, the consumer set inheriting the inheritedSet

    if (user) {
      consumerSet = results[user.id];

      if (groupSets.length) {
        unionedSet = PermissionUtil.union(groupSets);
        inheritedSet = PermissionUtil.inherit(unionedSet, publicSet);
        overallSet = PermissionUtil.inherit(consumerSet, inheritedSet);
      } else {
        overallSet = PermissionUtil.inherit(consumerSet, publicSet);
      }
    } else {
      //group or public
      if (groupSets.length) {
        overallSet = PermissionUtil.inherit(groupSets[0], publicSet);
      }
    }

    publicSet = this._renderPermissionSet(publicSet);
    groupSets = _.map(groupSets, this._renderPermissionSet, this);
    consumerSet = consumerSet ? this._renderPermissionSet(consumerSet) : null;
    unionedSet = unionedSet ? this._renderPermissionSet(unionedSet) : null;
    inheritedSet = inheritedSet
      ? this._renderPermissionSet(inheritedSet)
      : null;
    overallSet = overallSet ? this._renderPermissionSet(overallSet) : null;

    this.$(".public-section").append(publicSet.$el);

    if (groupSets.length) {
      this.$(".group-section").show();
      _.each(
        groupSets,
        function (groupSet, index) {
          this.$(".group-section").append(
            "<h3>" + groups[index].get("name") + "</h3>"
          );
          this.$(".group-section").append(groupSet.$el);
        },
        this
      );
    } else {
      this.$(".group-section").hide();
    }

    if (consumerSet) {
      this.$(".user-section").show().append(consumerSet.$el);
    } else {
      this.$(".user-section").hide();
    }

    if (unionedSet) {
      this.$(".group-section")
        .show()
        .append("<h3>Combined Group Permissions</h3>")
        .append(unionedSet.$el);
    } else {
      this.$(".group-section").hide();
    }

    if (inheritedSet) {
      this.$(".inherited-section").show().append(inheritedSet.$el);
    } else {
      this.$(".inherited-section").hide();
    }

    if (overallSet) {
      this.$(".overall-section").show().append(overallSet.$el);
    } else {
      this.$(".overall-section").hide();
    }
  },

  /**
   * Renders a permission set.
   * @param  permissionSet The permission set, as specified in api/models/Permission.
   * @return {Backbone.View}               A rendered view representing the permission set.
   */
  _renderPermissionSet: function (permissionSet) {
    var self = this;
    var PermissionSetModel = Backbone.Model.extend({
      typeDisplay: function () {
        var type = PermissionUtil.parseType(this.get("type")).type;

        return type || "*";
      },

      producerValue: function () {
        return PermissionUtil.parseType(this.get("type")).producer;
      },

      allowedValues: function () {
        var actions = PermissionUtil.findActions(
          permissionSet,
          this.get("type")
        );
        var results = _.keys(actions);

        results = _.filter(results, function (action) {
          return actions[action] ? true : false;
        });

        return results.join(", ");
      },

      deniedValues: function () {
        var actions = PermissionUtil.findActions(
          permissionSet,
          this.get("type")
        );
        var results = _.keys(actions);

        results = _.filter(results, function (action) {
          return actions[action] ? false : true;
        });

        return results.join(", ");
      },
    });

    var fields = [
      new Field({
        id: "typeDisplay",
        name: "typeDisplay",
        label: "Type",
        sortable: false,
        filterable: false,
      }),
      new Field({
        id: "producerValue",
        name: "producerValue",
        label: "Instance ID",
        editor: "custom",
        ViewerView: {
          loadingTemplate: _.template(
            '<span class="field-loading">Loading...</span>'
          ),

          errorTemplate: _.template(
            '<span class="field-error">Not Found</span>'
          ),

          template: _.template(
            '<% if(navUrl) {%><a href="<%- navUrl%>"><%-name%></a><%} else {%><%- name%><%}%>'
          ),

          startLoad: function () {
            var model = this.options.model;

            var type = PermissionUtil.parseType(model.get("type")).type;
            var metadata = producerMetadata(type);
            var modelValue = this.options.value;

            return ViewHelper.loadMetadata(metadata, modelValue);
          },
        },
        sortable: false,
        filterable: false,
      }),
      new Field({
        id: "allowedValues",
        name: "allowedValues",
        label: "Allowed Actions",
        sortable: false,
        filterable: false,
      }),
      new Field({
        id: "deniedValues",
        name: "deniedValues",
        label: "Prohibited Actions",
        sortable: false,
        filterable: false,
      }),
    ];

    var tableView = new CollectionTableView({
      fields: fields,
      collection: _.map(PermissionUtil.findTypes(permissionSet), function (
        type
      ) {
        return new PermissionSetModel({
          type: type,
        });
      }),
      allowSave: false,
      allowDelete: false,
      mode: "view",
    });

    tableView.render();

    return tableView;
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/admin/permissions/Viewer.js
// module id = 918
// module chunks = 1